import React from 'react';
import { Box, Button, Container, Typography, Paper, Stack, Alert } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import RedditIcon from '@mui/icons-material/Reddit';
import YouTubeIcon from '@mui/icons-material/YouTube';
import FacebookIcon from '@mui/icons-material/Facebook';
import ShieldIcon from '@mui/icons-material/Shield';
import { authService } from '../services/authService';

const LoginPage = () => {
  const [searchParams] = useSearchParams();
  const error = searchParams.get('error');
  const errorMessageMap = {
    facebook_not_configured: 'Facebook login is not configured on the backend. Set FACEBOOK_APP_ID, FACEBOOK_APP_SECRET, and FACEBOOK_REDIRECT_URI in Railway Variables.',
    facebook_login_failed: 'Facebook login failed. Please try again.',
    facebook_auth_failed: 'Facebook authentication failed during callback.',
    youtube_login_failed: 'YouTube login failed. Please try again.',
    youtube_auth_failed: 'YouTube authentication failed during callback.',
    state_mismatch: 'OAuth state mismatch. Please start login again.',
    missing_code: 'OAuth provider did not return an authorization code.',
    token_failed: 'Could not exchange OAuth code for access token.',
    user_info_failed: 'Could not fetch account profile from provider.',
  };

  const handleRedditLogin = () => { authService.loginWithReddit(); };
  const handleYouTubeLogin = () => { authService.loginWithYouTube(); };
  const handleFacebookLogin = () => { authService.loginWithFacebook(); };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#0B1220',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: { xs: 4, sm: 6 },
      }}
    >
      <Container maxWidth="sm">
        {/* Card */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3.5, sm: 5 },
            borderRadius: 4,
            bgcolor: '#111A2C',
            border: '1px solid rgba(148,163,184,0.18)',
            boxShadow: '0 24px 64px rgba(2,6,23,0.55)',
          }}
        >
          {/* Logo + title */}
          <Box sx={{ textAlign: 'center', mb: 4.5 }}>
            <Box
              sx={{
                display: 'inline-flex',
                p: 1.5,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #18B89A 0%, #3B82F6 100%)',
                mb: 2,
                boxShadow: '0 8px 24px rgba(24,184,154,0.3)',
              }}
            >
              <ShieldIcon sx={{ fontSize: 36, color: '#fff' }} />
            </Box>
            <Typography
              variant="h3"
              component="h1"
              sx={{
                fontWeight: 800,
                letterSpacing: '-0.03em',
                color: '#18B89A',
                mb: 0.5,
              }}
            >
              SafeSocial
            </Typography>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: '#93A4BD', mb: 1 }}
            >
              Social Platform Exposure Tracker
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: 'rgba(148,163,184,0.85)', maxWidth: 360, mx: 'auto', lineHeight: 1.65 }}
            >
              Connect your social media accounts to scan for privacy exposures
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessageMap[error] || `Login failed: ${error}`}
            </Alert>
          )}

          {/* Auth buttons */}
          <Stack spacing={2}>
            <Button
              variant="contained"
              size="large"
              fullWidth
              startIcon={<RedditIcon />}
              onClick={handleRedditLogin}
              sx={{
                backgroundColor: '#FF4500',
                '&:hover': { backgroundColor: '#E03D00', transform: 'translateY(-1px)', boxShadow: '0 6px 20px rgba(255,69,0,0.35)' },
                py: 1.6,
                textTransform: 'none',
                fontSize: '1.05rem',
                fontWeight: 700,
                borderRadius: 2.5,
                transition: 'all 0.2s ease',
              }}
            >
              Connect with Reddit
            </Button>

            <Button
              variant="contained"
              size="large"
              fullWidth
              startIcon={<YouTubeIcon />}
              onClick={handleYouTubeLogin}
              sx={{
                backgroundColor: '#FF0000',
                '&:hover': { backgroundColor: '#CC0000', transform: 'translateY(-1px)', boxShadow: '0 6px 20px rgba(255,0,0,0.35)' },
                py: 1.6,
                textTransform: 'none',
                fontSize: '1.05rem',
                fontWeight: 700,
                borderRadius: 2.5,
                transition: 'all 0.2s ease',
              }}
            >
              Connect with YouTube
            </Button>

            <Button
              variant="contained"
              size="large"
              fullWidth
              startIcon={<FacebookIcon />}
              onClick={handleFacebookLogin}
              sx={{
                backgroundColor: '#1877F2',
                '&:hover': { backgroundColor: '#1260CC', transform: 'translateY(-1px)', boxShadow: '0 6px 20px rgba(24,119,242,0.35)' },
                py: 1.6,
                textTransform: 'none',
                fontSize: '1.05rem',
                fontWeight: 700,
                borderRadius: 2.5,
                transition: 'all 0.2s ease',
              }}
            >
              Connect with Facebook
            </Button>
          </Stack>

          {/* Why OAuth box */}
          <Box
            sx={{
              mt: 4,
              p: 2.5,
              bgcolor: 'rgba(22,35,58,0.9)',
              borderRadius: 2.5,
              border: '1px solid rgba(148,163,184,0.14)',
            }}
          >
            <Typography variant="body2" sx={{ color: '#93A4BD', textAlign: 'center', lineHeight: 1.7 }}>
              <Box component="span" sx={{ fontWeight: 700, color: '#E6EDF7' }}>Why OAuth?</Box>
              <br />
              We never store your passwords. SafeSocial uses secure OAuth authentication,
              so you log in directly with the social platform and we only receive permission
              to read your public posts and comments.
            </Typography>
          </Box>

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: 'rgba(148,163,184,0.6)' }}>
              By connecting your account, you agree to our terms of service and privacy policy
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default LoginPage;
